import {
  createEntity,
  editEntity,
  deactivateEntity,
  updateEntityPoc,
  updateEntitySSO,
  activateEntity,
  recordEntityVisit,
} from "@/api/entities/entities.mutations";
import { EntitiesQueryKeys } from "@/api/entities/entities.queries";
import { useMutation, useQueryClient } from "@tanstack/react-query";
import { handleApiError } from "@/utils/responseHandler";
import type { ApiError } from "@/types/common.types";

export const useCreateEntity = () => {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: createEntity,
    onSuccess: () => {
      queryClient.invalidateQueries({
        queryKey: [EntitiesQueryKeys.list],
      });
    },
    onError: (error) => {
      handleApiError(error as ApiError);
    },
  });
};

export const useEditEntity = () => {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: editEntity,
    onSuccess: () => {
      queryClient.invalidateQueries({
        queryKey: [EntitiesQueryKeys.details],
      });
      queryClient.invalidateQueries({
        queryKey: [EntitiesQueryKeys.entityLabelDropdown],
      });
    },
    onError: (error) => {
      handleApiError(error as ApiError);
    },
  });
};

export const useUpdateEntityPoc = () => {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: updateEntityPoc,
    onSuccess: () => {
      queryClient.invalidateQueries({
        queryKey: [EntitiesQueryKeys.details],
      });
    },
    onError: (error) => {
      handleApiError(error as ApiError);
    },
  });
};

export const useDeactivateEntity = () => {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: deactivateEntity,
    onSuccess: () => {
      queryClient.invalidateQueries({
        queryKey: [EntitiesQueryKeys.list],
      });
    },
    onError: (error) => {
      handleApiError(error as ApiError);
    },
  });
};

export const useActivateEntity = () => {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: activateEntity,
    onSuccess: () => {
      queryClient.invalidateQueries({
        queryKey: [EntitiesQueryKeys.list],
      });
    },
    onError: (error) => {
      handleApiError(error as ApiError);
    },
  });
};

export const useUpdateEntitySSO = () => {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: updateEntitySSO,
    onSuccess: () => {
      queryClient.invalidateQueries({
        queryKey: [EntitiesQueryKeys.details],
      });
    },
    // onError is not needed here as the error is handled inline within the modal.
  });
};

// Fire-and-forget behind a picker switch (SERV-2793). The request itself is
// marked suppressErrorAlert, so the interceptor stays quiet; this onError only
// keeps the hook from routing the failure to handleApiError like its siblings,
// because the user has nothing to act on once the switch has happened.
export const useRecordEntityVisit = () => {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: recordEntityVisit,
    onSuccess: () => {
      queryClient.invalidateQueries({
        queryKey: [EntitiesQueryKeys.frequentEntities],
      });
    },
    onError: () => {},
  });
};
